import { Modal } from '@citizen-walk/shared-ui';
import { useMemo } from 'react';
import { useAnnouncements } from './useAnnouncements';

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

export interface AnnouncementHistoryModalProps {
  /** 現在地区 ID。この地区の announcements を購読する (必須)。省略すると phase2-default を読む不具合。 */
  districtId: string;
  open: boolean;
  onClose: () => void;
}

/**
 * お知らせ履歴 (過去 30 日分、アーカイブ済み含む) を一覧表示する Modal。
 *
 * 旧 `AnnouncementHistoryModal.tsx` (L3 参照、docs/97 §3 移植ログ) の構造を踏襲、
 * UI は shared-ui Modal を利用 (旧は手動 overlay、v2 では共通化)。
 *
 * subscription は useAnnouncements を再利用 (既存 banner と subscription を共有しない点に注意:
 * モーダル open ごとに新規 subscribe → 余分な listener。Phase 5 で Context 化を検討)。
 */
export function AnnouncementHistoryModal({
  districtId,
  open,
  onClose,
}: AnnouncementHistoryModalProps) {
  const { state } = useAnnouncements(districtId);

  const items = useMemo(() => {
    if (state.status !== 'ready') return [];
    // Date.now() を memo 内で許容: cutoff は表示時刻基準で十分 (秒精度不要)
    // eslint-disable-next-line react-hooks/purity
    const cutoff = new Date(Date.now() - THIRTY_DAYS_MS);
    return state.items.filter((a) => !a.startAt || a.startAt >= cutoff);
  }, [state]);

  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose} title="📣 お知らせ履歴" size="md">
      {state.status === 'loading' && <p className="text-slate-500 text-center py-6">読込中…</p>}
      {state.status === 'error' && (
        <p className="text-error-700 text-center py-6">取得エラー: {state.error.message}</p>
      )}
      {state.status === 'ready' && items.length === 0 && (
        <p className="text-slate-500 text-center py-6">お知らせはまだありません。</p>
      )}
      {state.status === 'ready' && items.length > 0 && (
        <ul className="divide-y divide-slate-200">
          {items.map((a) => (
            <li key={a.id} className="py-3 first:pt-0 last:pb-0">
              <div className="text-xs text-slate-500 mb-1">
                {a.startAt?.toLocaleString('ja-JP') ?? ''}
                {a.archivedAt && ' (終了)'}
              </div>
              {a.title && <p className="font-medium mb-1">{a.title}</p>}
              <p className="whitespace-pre-wrap text-sm text-slate-700">{a.body}</p>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
